<script setup lang="ts">
import sidebar from './sidebar.vue'
import stepType from './steps/step-type.vue'
import stepTypeNoon from './steps/step-type-noon.vue'
import stepDate from './steps/step-date.vue'
import stepMenu from './steps/step-menu.vue'
import stepMenu2 from './steps/step-menu2.vue'
import stepDrinks from './steps/step-drinks.vue'
import stepOptions from './steps/step-options.vue'
import stepInfo from './steps/step-info.vue'
import { onMounted, ref, watch } from 'vue';
import { useBookingStepStore } from '@/stores/bookingStep'
import { Steps } from '@/api/types/steps.enum'
import StepSummary from './steps/step-summary.vue'
import { useRoute, useRouter } from 'vue-router'
import { Mode } from '@/api/types/mode.enum'
import { useBookingDataStore } from '@/stores/bookingData'
import StepSummaryMenu from './steps/step-summary-menu.vue'
import { validateNoonInvite } from '@/api/api';
import { formatDate, formatDatePadded } from '@/utils.ts/date';
import { analytics, stepLabel } from '@/analytics';
import { useDateAvailabilityWatch } from '@/composables/useDateAvailabilityWatch';

const step = useBookingStepStore();
const bookingData = useBookingDataStore();
const router = useRouter();
const route = useRoute();

const noonInviteError = ref('');
// The wizard steps read the mode (and the menu link's token) in their own
// onMounted, which runs before this component's. Don't mount them until the
// URL has been applied, or step-menu2 would run in the wrong mode — e.g. a
// menu link opened while the visitor's own cart sits on the dish step would
// never load the linked booking and save the visitor's cart into it.
const urlModeApplied = ref(false);

onMounted(async () => {
  // router.isReady() rejects when the (never displayed) route component's
  // chunk fails to load; read the URL directly then, so the wizard still opens.
  const query: Record<string, unknown> = await router.isReady().then(
    () => route.query,
    () => Object.fromEntries(new URLSearchParams(window.location.search)),
  );
  const initialStep = step.step;
  if (query.mode == Mode.Menu.toLowerCase()) {
    bookingData.setBookingId(query.token as string);
    step.setBookingId(query.token as string);
    step.setMode(Mode.Menu);
  } else {
    step.setMode(Mode.Normal);
  }
  urlModeApplied.value = true;

  if (query.mode === Mode.Noon.toLowerCase()) {
    const token = query.token as string;
    if (!token) {
      noonInviteError.value = 'Lien d\'invitation invalide.';
    } else {
      const result = await validateNoonInvite(token);
      if (result?.success && result.data) {
        // Drop any cart/client state hydrated from a prior normal-booking
        // session in this browser before applying the invite. Otherwise the
        // noon payload could still carry an evening service (diner/logement) —
        // which the backend 422s — with no way to clear it from the noon flow.
        bookingData.resetCart();
        // Set venue before updateDate so updateDate's persist() captures the
        // real venue (otherwise localStorage briefly holds venue:'' from the
        // reset until the next persisting mutation).
        bookingData.venue = result.data.venue as 'lasalle' | 'serre';
        bookingData.updateDate(new Date(result.data.date), 'base', 0, false);
        bookingData.noonInviteToken = token;
        step.setMode(Mode.Noon);
      } else {
        noonInviteError.value = 'Ce lien d\'invitation est invalide ou a expiré.';
      }
    }
  }

  if(query.step == 'stripe') {
    console.log('stripe callback', query.session_id);
    step.checkoutId = query.session_id as string;
    step.goToStep(Steps.Confirmation, true);
  } else if(step.step == Steps.Confirmation) {
    step.reset();
  }

  // Entry point for the normal fresh-load path only — the menu/noon/stripe
  // branches above already dispatch a stepChanged (via setMode/goToStep),
  // which main.ts's listener turns into step_view. Without this, a visitor
  // who lands on the Date step and leaves emits zero events (no stepChanged
  // fires for a plain setMode(Normal) unless coming from SummaryMenu).
  // Also exclude a persisted SummaryMenu/Confirmation step: setMode(Normal)
  // internally goToSteps away from SummaryMenu, and a stale Confirmation step
  // without ?step=stripe hits step.reset() above — both already dispatch a
  // stepChanged that main.ts turns into step_view, so emitting here too would
  // double-fire step_view (and a spurious wizard_start) on reload.
  if (
    query.mode !== Mode.Menu.toLowerCase() &&
    query.mode !== Mode.Noon.toLowerCase() &&
    query.step !== 'stripe' &&
    initialStep !== Steps.SummaryMenu &&
    initialStep !== Steps.Confirmation
  ) {
    analytics.track('wizard_start', { mode: 'normal', venue: bookingData.venue || null });
    analytics.track('step_view', { step: stepLabel(String(step.step)), mode: 'normal', venue: bookingData.venue || null });
  }
})

// allowMenu is otherwise only refreshed while the Type step renders; after a
// reload mid-wizard it would be stale, and the navigation skips the menu and
// drinks steps when it is false. Re-derive it from the cart first.
function nextStep() {
  bookingData.canGoMenu();
  step.nextStep();
}

function previousStep() {
  bookingData.canGoMenu();
  step.previousStep();
}

function goToStep(newStep: Steps) {
  console.log('goToStep', newStep);
  step.goToStep(newStep);
}

// While a date is selected in the normal flow, re-verify it is still free
// (every couple of minutes and whenever the tab is refocused). If it has been
// taken in the meantime, surface it and send the customer back to pick a new
// date — keeping the rest of their cart intact.
const showDateUnavailableDialog = ref(false);

useDateAvailabilityWatch(() => {
  // Funnel milestone: the chosen date was taken while the customer was mid-flow.
  // The watcher only arms in normal watchable states, so this is always normal mode.
  analytics.track('date_lost', { mode: 'normal', venue: bookingData.venue || null });
  showDateUnavailableDialog.value = true;
});

function handleDateUnavailableDismiss() {
  showDateUnavailableDialog.value = false;
  bookingData.clearDateForReselection();
  step.goToStep(Steps.Date, true);
}

// Menu-choice cutoff: when the deadline has passed the customer gets the
// chef's surprise selection. Inform them once when the menu link is opened.
const showSurpriseDialog = ref(false);

watch(
  () => bookingData.menuChoiceClosed,
  (closed) => {
    // Only ever surface the cutoff notice in menu-link mode — never let leaked
    // state pop this dialog during the normal booking flow.
    if (closed && step.getMode() == Mode.Menu) {
      showSurpriseDialog.value = true;
    }
  }
);
</script>

<template>
  <v-dialog v-model="showDateUnavailableDialog" persistent width="auto" scroll-strategy="none" class="dialog-viewport-center">
    <v-card max-width="500" prepend-icon="mdi-calendar-remove"
      title="Date indisponible"
      text="La date sélectionnée n'est plus disponible. Veuillez choisir une autre date.">
      <template v-slot:actions>
        <v-btn color="primary" @click="handleDateUnavailableDismiss">
          Choisir une autre date
        </v-btn>
      </template>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showSurpriseDialog" persistent width="auto" scroll-strategy="none" class="dialog-viewport-center">
    <v-card max-width="560" prepend-icon="mdi-silverware-fork-knife"
      title="Choix des plats clôturé">
      <v-card-text>
        Le délai pour communiquer vos préférences est écoulé. Notre chef se fera un plaisir de vous
        surprendre avec sa «&nbsp;Sélection du Chef&nbsp;», une composition inspirée de la carte du moment.
        <br /><br />
        Si vous avez un aliment que vous ne souhaitez vraiment pas retrouver dans votre assiette, merci de
        nous le signaler par e-mail à <a href="mailto:diner@eauget.com">diner@eauget.com</a>.
      </v-card-text>
      <template v-slot:actions>
        <v-btn color="primary" @click="showSurpriseDialog = false">
          J'ai compris
        </v-btn>
      </template>
    </v-card>
  </v-dialog>
  <!-- A failed noon invite is a dead end: render ONLY the error, never the
       wizard, so an invited guest can't accidentally complete a normal
       evening booking from an invite URL. -->
  <div v-if="noonInviteError" class="noon-invite-error">
    <v-alert type="error" :text="noonInviteError" />
  </div>
  <template v-else-if="urlModeApplied">
    <sidebar v-if="step.getMode() == Mode.Normal && step.step !== Steps.Confirmation" :step="step.step" :date-label="bookingData.date ? formatDatePadded(bookingData.date) : ''" @goToStep="goToStep" data-testid="sidebar" />
    <!-- Noon (invite) mode renders no sidebar, so keep a lightweight date banner
         there only; in normal mode the date now lives in the sidebar's Date item. -->
    <div
      v-if="bookingData.date && step.step !== Steps.Date && step.step !== Steps.Confirmation && step.getMode() === Mode.Noon"
      class="booking-date-banner"
      data-testid="booking-date-banner"
    >
      Votre réservation&nbsp;: {{ formatDate(bookingData.date) }}
    </div>
    <stepDate @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Date"></stepDate>
    <stepType     @next-step="nextStep" @previous-step="previousStep" v-if="step.step === Steps.Type && step.getMode() !== Mode.Noon" />
    <stepTypeNoon @next-step="nextStep" @previous-step="previousStep" v-if="step.step === Steps.Type && step.getMode() === Mode.Noon" />
    <stepMenu @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Menu"></stepMenu>
    <stepMenu2 @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Menu2"></stepMenu2>
    <stepDrinks @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Drinks"></stepDrinks>
    <stepOptions @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Options"></stepOptions>
    <stepInfo @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Info"></stepInfo>
    <StepSummary @next-step="nextStep" @previous-step="previousStep" v-if="step.step == Steps.Summary"></StepSummary>
    <StepSummaryMenu v-if="step.step == Steps.SummaryMenu"></StepSummaryMenu>
    <StepConfirmation v-if="step.step == Steps.Confirmation"></StepConfirmation>
  </template>
</template>

<style scoped>
h1 {
  font-weight: 500;
  font-size: 2.6rem;
  position: relative;
  top: -10px;
}

h3 {
  font-size: 1.2rem;
}

.greetings h1,
.greetings h3 {
  text-align: center;
}

@media (min-width: 1024px) {

  .greetings h1,
  .greetings h3 {
    text-align: left;
  }
}
</style>

<style>
.dialog-viewport-center .v-overlay__scrim,
.dialog-viewport-center .v-overlay__content {
  position: fixed !important;
}
.booking-date-banner {
  padding: 8px 16px;
  font-size: 0.9rem;
  color: var(--v-theme-on-surface, #333);
  border-bottom: 1px solid var(--v-border-color, #e0e0e0);
}
.noon-invite-error {
  padding: 16px;
}
</style>
